import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  TableContainer,
  TableCell,
  Paper,
  TableHead,
  TableRow,
  Typography,
  Table,
  TableBody,
  Skeleton,
  TablePagination,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import Row from "./Row";
import { saveAs } from "file-saver";
import moment from "moment";
import Theme from "../../Themes/Theme";
import { useAuthContext } from "../../context/AuthContext";
import Login from "../../pages/Login";

const TableComponent = ({ searchFilters }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    totalCount: 0,
  });

  const { setNotification } = useAuthContext();

  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownloadReport = async () => {
    setDownloadLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/downloadPayment`,
        {
          responseType: "blob",
          params: {
            tab: searchParams.get("tab"),
            filters: searchFilters,
          },
          withCredentials: true,
        },
      );

      const filename = `${moment().format("YYYY/MM/DD_HH:mm")}_${searchParams.get("tab") == "unpaid" ? "Απληρωτες" : "Πληρωμένες"}_Οφειλές.xlsx`;
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      saveAs(blob, filename);
    } catch (error) {
      setNotification({
        open: true,
        color: "error",
        message: error.response.data.message,
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleChangePage = async (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
    getPayments(searchFilters, { ...pagination, page: newPage });
  };

  const handleChangeRowsPerPage = async (event) => {
    setPagination({
      ...pagination,
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    });
    getPayments(searchFilters, {
      ...pagination,
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    });
  };

  const getPayments = async (filters, pagination) => {
    setLoading(true);
    setRows(null); //refresh rows
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/payments`,
        {
          params: {
            tab: searchParams.get("tab") ? searchParams.get("tab") : "unpaid",
            filters: filters,
            pagination: pagination,
          },
          withCredentials: true,
        },
      );
      setRows(response.data.payments);
      setPagination({ ...pagination, totalCount: response.data.count });
    } catch (e) {
      setNotification({
        open: true,
        color: "error",
        message: e.response.data.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPagination({ ...pagination, page: 0 });
    getPayments(searchFilters, { ...pagination, page: 0 });
  }, [searchParams.get("tab"), searchFilters]);

  return (
    <Box sx={{ pb: 5, ml: 3, mt: 3, maxWidth: "100vw" }}>
      <TableContainer
        component={Paper}
        sx={{ width: "95%", maxHeight: "60vh" }}
      >
        <Table
          stickyHeader
          sx={{
            "& .MuiTableCell-head": {
              backgroundColor: "#EDEDED",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center">
                <Typography variant="h5">ID Οφειλής</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">ΑΦΜ</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">Αρ.ΓΕΜΗ</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">Ποσό</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">Κατάσταση</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">Επιμελητήριο</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">Έτος Οφειλής</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">Ημερομηνία Βεβαίωσης</Typography>
              </TableCell>
              {searchParams.get("tab") == "paid" && (
                <TableCell align="center">
                  <Typography variant="h5">Ημερομηνία Πληρωμής</Typography>
                </TableCell>
              )}
              <TableCell align="center">
                <Typography variant="h5">Πληρωμές</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ width: "100%" }}>
            {loading || !rows ? (
              Array.from(
                { length: pagination.rowsPerPage },
                (_, index) => index + 1,
              ).map((value, index) => (
                <TableRow key={index} sx={{ width: "100%", p: 0 }}>
                  {Array.from(
                    { length: searchParams.get("tab") == "unpaid" ? 9 : 10 },
                    (_, index) => index + 1,
                  ).map((column, index1, array) => (
                    <TableCell key={index1} align="center" TableCell>
                      <Typography variant="h3">
                        <Skeleton
                          key={index}
                          sx={{ borderRadius: "10px" }}
                          variant="rectangular"
                          animation={
                            index1 == 0
                              ? "pulse"
                              : index1 === array.length - 1
                                ? false
                                : "wave"
                          }
                        />
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length > 0 ? (
              rows.map((row, index) => <Row key={index} row={row} />)
            ) : (
              <TableRow sx={{ p: 0, borderBottom: "unset", width: "100%" }}>
                <TableCell
                  style={{ p: 10 }}
                  align="center"
                  colSpan={searchParams.get("tab") == "unpaid" ? 8 : 9}
                >
                  Δεν Βρέθηκαν Οφείλές
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          pl: 0,
          flexDirection: "row",
          width: "95%",
          pt: 3,
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          sx={{
            width: "9rem",
            justifySelf: "flex-start",
            "&:disabled": {
              backgroundColor: Theme.palette.primary.main,
              color: "white",
            },
          }}
          disabled={downloadLoading}
          onClick={handleDownloadReport}
        >
          {downloadLoading ? (
            <CircularProgress size={25} sx={{ color: "white" }} />
          ) : (
            <Typography variant="body2">Λήψη Αρχείου</Typography>
          )}
        </Button>
        <TablePagination
          sx={{ backgroundColor: "white", width: "90%" }}
          rowsPerPageOptions={[10, 50, 100]}
          component="div"
          count={pagination.totalCount}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          // backIconButtonProps=
          backIconButtonProps={{
            disabled: loading || pagination.page == 0,
          }}
          nextIconButtonProps={{
            disabled: loading,
          }}
          SelectProps={{
            disabled: loading,
          }}
        />
      </Box>
    </Box>
  );
};
1;
export default TableComponent;
